import {
  Box,
  Button,
  createStyles,
  Group,
  List,
  Modal,
  Skeleton,
  Stack,
  Text,
  Title,
  useMantineTheme
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useRouter } from 'next/router'
import React, { useState } from 'react'

import { DateField } from '@/components/commons/DisplayData'
import { useFetch } from '@/lib/hooks'
import { api, errorHandler, getUrlString, notificationHandler } from '@/lib/utils'
import { useAuth } from '@/providers/AuthProvider'
import { Site as SiteType } from '@/types'
import { Policy as PolicyType } from '@/types/policy'

const useStyles = createStyles(theme => ({
  title: {
    backgroundColor: 'white',
    position: 'sticky',
    top: 0
  },
  item: {
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    '&:last-child': { borderBottom: 'none' }
  },
  itemWrapper: { width: '100%' }
}))

interface Data {
  site?: SiteType
}

interface Props {
  initialData?: Data
  children?: React.ReactNode
}

export default function AcceptAgreementPopup({ initialData }: Props) {
  // Styles
  const { isAuthenticated } = useAuth() || {}
  const { classes } = useStyles()
  const theme = useMantineTheme()
  const isXs = useMediaQuery(`(max-width: ${theme.breakpoints.xs}px)`)

  // Hooks
  const router = useRouter()
  const { site } = router.query || {}
  const siteSlug = getUrlString(site)

  // Fetch
  const { data: siteData } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site
  })
  const {
    data: policies,
    isLoading,
    mutate
  } = useFetch([
    siteSlug && siteData?.seller?.slug && isAuthenticated
      ? `/${siteSlug}/policies/${siteData.seller.slug}/`
      : null
  ])
  const termsOfUse: PolicyType = policies?.find(
    (policy: PolicyType) =>
      policy.tags.includes('terms-of-use') && policy.content && !policy.content?.isAccepted
  )
  const privacyPolicy: PolicyType = policies?.find(
    (policy: PolicyType) =>
      policy.tags.includes('privacy-policy') && policy.content && !policy.content?.isAccepted
  )

  // States
  const [isAccepting, setIsAccepting] = useState(false)

  // Actions
  const renderDrawerSkeleton = () => (
    <Stack spacing="md">
      {[1, 2, 3].map(i => (
        <Box key={i}>
          <Skeleton height={24} width="70%" mb="xs" />
          <Skeleton height={12} width="40%" mb="sm" />
          <Skeleton height={16} width="100%" mb="xs" />
          <Skeleton height={16} width="95%" mb="xs" />
          <Skeleton height={16} width="85%" mb="md" />
        </Box>
      ))}
    </Stack>
  )

  const handleAcceptAgreements = () => {
    const acceptPoliciesUid: string[] = []
    if (termsOfUse) acceptPoliciesUid.push(termsOfUse.uid)
    if (privacyPolicy) acceptPoliciesUid.push(privacyPolicy.uid)

    setIsAccepting(true)

    return api
      .post(`/${siteSlug}/policies/${siteData?.seller?.slug}/accept-agreement/`, {
        policies: acceptPoliciesUid
      })
      .then(() =>
        mutate(
          (currentPolicies: PolicyType[] = []) =>
            currentPolicies.map(policy =>
              acceptPoliciesUid.includes(policy.uid)
                ? { ...policy, content: { ...policy.content, isAccepted: true } }
                : policy
            ),
          { revalidate: true }
        )
      )
      .catch((error: any) => {
        const errors = errorHandler(error?.response?.data)
        return notificationHandler({
          variant: 'error',
          message:
            `${errors?.fields?.[0]}: ${errors?.messages?.[0]}` ||
            'Houve um erro ao aceitar os termos. Tente novamente ou entre em contato conosco.'
        })
      })
      .finally(() => setIsAccepting(false))
  }

  if (!isAuthenticated) return null

  return (
    <Modal
      opened={!!termsOfUse || !!privacyPolicy}
      padding="lg"
      size={isXs ? 'full' : 'xl'}
      onClose={() => {}}
      centered
      withCloseButton={false}
      zIndex={400}
      title={
        <Title order={3}>
          {!!termsOfUse && 'Termos de Uso'}
          {!!termsOfUse && !!privacyPolicy && ' e '}
          {!!privacyPolicy && 'Política de Privacidade'}
        </Title>
      }>
      <Stack>
        <Box style={{ maxHeight: 'calc(70vh - 100px)', overflow: 'auto' }}>
          {isLoading ? (
            renderDrawerSkeleton()
          ) : (
            <List
              listStyleType="none"
              classNames={{ item: classes.item, itemWrapper: classes.itemWrapper }}>
              {!!termsOfUse && (
                <List.Item>
                  <Stack spacing="sm" py="md">
                    <Title order={4} mb={0}>
                      {termsOfUse.title}
                    </Title>
                    {termsOfUse.dateUpdated && (
                      <Text size="xs" color="dimmed">
                        Atualizado em <DateField type="date" value={termsOfUse.dateUpdated} />
                      </Text>
                    )}
                    <Text
                      size="sm"
                      color="dimmed"
                      dangerouslySetInnerHTML={{
                        __html: termsOfUse.content?.html || ''
                      }}
                    />
                  </Stack>
                </List.Item>
              )}
              {!!privacyPolicy && (
                <List.Item>
                  <Stack spacing="sm" py="md">
                    <Title order={4} mb={0}>
                      {privacyPolicy.title}
                    </Title>
                    {privacyPolicy.dateUpdated && (
                      <Text size="xs" color="dimmed">
                        Atualizado em <DateField type="date" value={privacyPolicy.dateUpdated} />
                      </Text>
                    )}
                    <Text
                      size="sm"
                      color="dimmed"
                      dangerouslySetInnerHTML={{
                        __html: privacyPolicy.content?.html || ''
                      }}
                    />
                  </Stack>
                </List.Item>
              )}
            </List>
          )}
        </Box>
        <Text>
          Ao continuar navegando, você concorda com
          {!!termsOfUse && ' nossos Termos de Uso'}
          {!!termsOfUse && !!privacyPolicy && ' e'}
          {!!privacyPolicy && ' nossa Política de Privacidade'}.
        </Text>
        <Group position="right">
          <Button color="green" loading={isAccepting} onClick={handleAcceptAgreements}>
            Aceitar e continuar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
